import {
    api,
    ApiError,
    apiErrorMessage,
    oneSpec,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    Banner,
    Button,
    Checkbox,
    ConfirmDialog,
    DateTime,
    GraphCanvas,
    KeyValueList,
    Label,
    Money,
    ObjectRef,
    PageHeader,
    PublicId,
    ReasonDialog,
    Section,
    StatusBadge,
    statusLabel,
    Textarea,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { RiskCaseDetail, RiskCaseNote } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';
import { caseHref } from '@/pages/risk/reviews';

/** The reads of `/risk/reviews/:id` (BM-46 §2.0); the id may be the case's or its Payments review's (crv_). */
export const riskCaseReads = (id: string | undefined) => ({
    case: oneSpec(`/staff/risk/cases/${id ?? ''}`),
});

export const keyHref = (kind: string, key: string) =>
    `/risk/keys/${kind}/${encodeURIComponent(key)}`;

type Decision = 'approve' | 'bypass' | 'decline';

const COPY: Record<
    Decision,
    { title: string; description: string; confirm: string }
> = {
    approve: {
        title: 'Approve this payment',
        description:
            'The held amount is captured in full and the order goes ahead. The check is labelled approved.',
        confirm: 'Approve',
    },
    bypass: {
        title: 'Bypass the rules on this payment',
        description:
            'The held amount is captured as on an approve; the case is recorded as bypassed, with your name and the reason.',
        confirm: 'Bypass',
    },
    decline: {
        title: 'Decline this payment',
        description:
            'The hold is released: the buyer is not charged and the order is cancelled. The keys you tick are flagged with the reason.',
        confirm: 'Decline',
    },
};

/** Decision 26: the check's keys a decline may flag; email and payment account are ticked when the dialog opens. */
const FLAGGABLE = [
    ['email', 'Email'],
    ['payment_account', 'Card'],
    ['ip', 'IP address'],
    ['device', 'Device'],
] as const;

const KIND_LABEL: Record<string, string> = {
    email: 'Email',
    payment_account: 'Card',
    ip: 'IP address',
    device: 'Device',
    account: 'Account',
    bin: 'BIN',
};

/**
 * BM-125 slice 2: one review case with what a decision needs, and the decision. The first decision wins: a stale
 * version answers 409 version_mismatch, the dialog closes, the case is read again and the conflict banner says why.
 * One Idempotency-Key per decision dialog, kept for a resubmit after no response, replaced after an HTTP error.
 */
export function RiskCasePage() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const can = useAuthStore((s) => s.can);
    const me = useMe();
    const path = riskCaseReads(id).case.path;
    const query = useOne<RiskCaseDetail>(path);
    const [decision, setDecision] = useState<{
        kind: Decision;
        key: string;
    } | null>(null);
    const [flags, setFlags] = useState<string[]>(['email', 'payment_account']);
    const [conflict, setConflict] = useState(false);
    const [note, setNote] = useState('');
    const invalidate = [
        '/staff/risk/cases',
        '/staff/charge-reviews',
        '/staff/charges',
        '/staff/queues',
    ];
    const decide = useWrite(
        async (body: {
            kind: Decision;
            key: string;
            reason: string;
            version: number;
            flagKeys: string[];
        }) =>
            (
                await api().post(
                    `/staff/risk/cases/${id}/${body.kind}`,
                    body.kind === 'decline'
                        ? {
                              reason: body.reason,
                              version: body.version,
                              flag_keys: body.flagKeys,
                          }
                        : { reason: body.reason, version: body.version },
                    { idempotencyKey: body.key },
                )
            ).data,
        invalidate,
    );
    const assign = useWrite(
        async (assignee: string | null) =>
            (
                await api().post(`/staff/risk/cases/${id}/assign`, {
                    assigned_to: assignee,
                })
            ).data,
        invalidate,
    );
    const addNote = useWrite(
        async (body: string) =>
            (
                await api().post<RiskCaseNote>(
                    `/staff/risk/cases/${id}/notes`,
                    { body },
                )
            ).data,
        [path],
    );
    const open = (kind: Decision) => {
        decide.reset();
        setFlags(['email', 'payment_account']);
        setDecision({ kind, key: crypto.randomUUID() });
    };
    const refused = (error: unknown) => {
        // no response (a network error): the key stays, so the resubmit replays the same decision
        if (!ApiError.is(error)) return;
        if (error.status === 409) {
            setDecision(null);
            setConflict(true);
            void query.refetch();
            return;
        }
        setDecision((d) => (d ? { ...d, key: crypto.randomUUID() } : d));
        // self-review 2026-10-31: a refused capture or void released the server's claim at a new version; the case is
        // read again so the dialog's next confirm carries it (the old page re-read on every HTTP error too)
        void query.refetch();
    };
    const decider = can(P.riskReviewsDecide);
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Review', href: '/risk/reviews' }]}
                {...detailTitle(query, null, id, 'Case')}
                meta={
                    <>
                        <PublicId
                            id={query.data?.data.id ?? id}
                            short={false}
                        />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
            />
            <DetailBody query={query} title="Review case">
                {(c) => (
                    <div className="space-y-4">
                        {conflict ? (
                            <Banner
                                tone="warning"
                                title="This case changed since you opened it"
                                data-testid="case-conflict"
                            >
                                Someone decided or assigned it first. It is
                                shown as it is now; decide again only if it is
                                still open.
                            </Banner>
                        ) : null}
                        <Section
                            title="Held payment"
                            action={
                                c.state === 'open' && decider ? (
                                    <div
                                        className="flex flex-wrap gap-2"
                                        data-testid="case-actions"
                                    >
                                        <Button
                                            size="sm"
                                            onClick={() => open('approve')}
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => open('decline')}
                                        >
                                            Decline
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => open('bypass')}
                                        >
                                            Bypass
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            disabled={assign.isPending}
                                            onClick={() => {
                                                setConflict(false);
                                                assign.mutate(
                                                    c.assigned_to === me
                                                        ? null
                                                        : me,
                                                    {
                                                        onError: (e) => {
                                                            if (
                                                                ApiError.is(
                                                                    e,
                                                                ) &&
                                                                e.status === 409
                                                            ) {
                                                                setConflict(
                                                                    true,
                                                                );
                                                                void query.refetch();
                                                            }
                                                        },
                                                    },
                                                );
                                            }}
                                        >
                                            {c.assigned_to === me
                                                ? 'Unassign'
                                                : 'Assign to me'}
                                        </Button>
                                    </div>
                                ) : null
                            }
                        >
                            <KeyValueList
                                items={[
                                    {
                                        label: 'State',
                                        value: (
                                            <span className="flex flex-wrap items-center gap-2">
                                                <StatusBadge
                                                    status={c.state}
                                                    domain={
                                                        ADMIN_STATUSES.riskCase
                                                    }
                                                />
                                                {c.late ? (
                                                    <Badge variant="danger">
                                                        Late
                                                    </Badge>
                                                ) : null}
                                            </span>
                                        ),
                                    },
                                    {
                                        label: 'Kind',
                                        value: (
                                            <StatusBadge
                                                status={c.kind}
                                                domain={
                                                    ADMIN_STATUSES.riskCaseKind
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        label: 'Opened because',
                                        value: statusLabel(
                                            c.opened_reason ?? '',
                                        ),
                                    },
                                    {
                                        label: 'Merchant',
                                        value: <ObjectRef id={c.merchant_id} />,
                                    },
                                    {
                                        label: 'Charge',
                                        value: <ObjectRef id={c.charge_id} />,
                                    },
                                    c.session_id
                                        ? {
                                              label: 'Session',
                                              value: (
                                                  <ObjectRef
                                                      id={c.session_id}
                                                  />
                                              ),
                                          }
                                        : null,
                                    {
                                        label: 'Amount',
                                        value: (
                                            <Money
                                                cents={c.amount_cents}
                                                currency={c.currency}
                                            />
                                        ),
                                        num: true,
                                    },
                                    {
                                        label: 'Opened',
                                        value: (
                                            <DateTime
                                                zone="inline"
                                                value={c.opened_at}
                                                empty="—"
                                            />
                                        ),
                                    },
                                    {
                                        label: `Due (${c.sla_hours ?? 24} h)`,
                                        value: (
                                            <DateTime
                                                zone="inline"
                                                value={c.sla_due_at}
                                                empty="—"
                                            />
                                        ),
                                    },
                                    {
                                        label: 'Voids if undecided',
                                        value: (
                                            <DateTime
                                                zone="inline"
                                                value={c.expires_at}
                                                empty="—"
                                            />
                                        ),
                                    },
                                    {
                                        label: 'Assigned to',
                                        value: who(c.assigned_to, me),
                                    },
                                    c.decided_at
                                        ? {
                                              label: 'Decided',
                                              value: (
                                                  <>
                                                      {who(c.decided_by, me)},{' '}
                                                      <DateTime
                                                          zone="inline"
                                                          value={c.decided_at}
                                                          empty="—"
                                                      />
                                                  </>
                                              ),
                                          }
                                        : null,
                                    c.reason
                                        ? { label: 'Reason', value: c.reason }
                                        : null,
                                    (c.flagged_keys ?? []).length > 0
                                        ? {
                                              label: 'Flagged',
                                              value: (c.flagged_keys ?? [])
                                                  .map(
                                                      (k) => KIND_LABEL[k] ?? k,
                                                  )
                                                  .join(', '),
                                          }
                                        : null,
                                ]}
                            />
                        </Section>

                        <Section
                            title="Verdict and rules"
                            description="Each stage of the check, the score against the bands and every rule that hit, with its explanation."
                        >
                            {c.checks.length === 0 ? (
                                <p className="text-fg-2 text-sm">
                                    No check is linked to this payment (a hold
                                    Payments parked without one).
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {c.checks.map((k) => (
                                        <div
                                            key={k.id}
                                            data-testid={`check-${k.stage}`}
                                            className="space-y-2"
                                        >
                                            <p className="text-sm">
                                                <span className="font-medium">
                                                    {statusLabel(k.stage)}
                                                </span>
                                                : {statusLabel(k.verdict)},
                                                score{' '}
                                                <span className="tabular-nums">
                                                    {k.score}
                                                </span>
                                                {k.bands ? (
                                                    <span className="text-fg-2">
                                                        {' '}
                                                        (review at{' '}
                                                        {String(
                                                            (
                                                                k.bands as Record<
                                                                    string,
                                                                    number
                                                                >
                                                            ).review ?? '—',
                                                        )}
                                                        , decline at{' '}
                                                        {String(
                                                            (
                                                                k.bands as Record<
                                                                    string,
                                                                    number
                                                                >
                                                            ).decline ?? '—',
                                                        )}
                                                        )
                                                    </span>
                                                ) : null}
                                            </p>
                                            {k.ml_score != null ? (
                                                <p
                                                    className="text-fg-2 m-0 text-sm"
                                                    data-testid="check-ml"
                                                >
                                                    Model score{' '}
                                                    <span className="text-fg-1 tabular-nums">
                                                        {k.ml_score}
                                                    </span>{' '}
                                                    (percentile of the model's
                                                    validation set)
                                                    {k.ml_top_features?.length
                                                        ? `: ${k.ml_top_features.map((f) => `${f.feature}${f.value == null ? '' : ` = ${String(f.value)}`}`).join(', ')}`
                                                        : null}
                                                </p>
                                            ) : null}
                                            {k.explanations.length === 0 ? (
                                                <p className="text-fg-2 text-sm">
                                                    No rule hit.
                                                </p>
                                            ) : (
                                                <ul className="m-0 list-none space-y-1 p-0 text-sm">
                                                    {k.explanations.map(
                                                        (e, i) => (
                                                            <li
                                                                key={`${e.key ?? i}`}
                                                                className="flex flex-wrap gap-2"
                                                            >
                                                                <span className="font-mono text-xs">
                                                                    {e.key}
                                                                </span>
                                                                <span>
                                                                    {e.explanation ||
                                                                        e.name}
                                                                </span>
                                                                {e.action ? (
                                                                    <Badge variant="warning">
                                                                        {statusLabel(
                                                                            e.action,
                                                                        )}
                                                                    </Badge>
                                                                ) : null}
                                                                {e.score ? (
                                                                    <span className="text-fg-2 tabular-nums">
                                                                        +
                                                                        {
                                                                            e.score
                                                                        }
                                                                    </span>
                                                                ) : null}
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Section>

                        <Section
                            title="Authorization"
                            description="What the processor answered on the hold."
                        >
                            <KeyValueList
                                items={[
                                    {
                                        label: 'AVS',
                                        value:
                                            c.authorization.avs_result ?? '—',
                                        mono: true,
                                    },
                                    {
                                        label: 'CVV',
                                        value:
                                            c.authorization.cvv_result ?? '—',
                                        mono: true,
                                    },
                                    {
                                        label: '3-D Secure',
                                        value: c.authorization.three_ds ?? '—',
                                    },
                                    {
                                        label: 'Liability shift',
                                        value:
                                            c.authorization.liability_shift ===
                                                null ||
                                            c.authorization.liability_shift ===
                                                undefined
                                                ? '—'
                                                : c.authorization
                                                        .liability_shift
                                                  ? 'Yes'
                                                  : 'No',
                                    },
                                    {
                                        label: 'Issuer response',
                                        value:
                                            c.authorization.issuer_response ??
                                            '—',
                                        mono: true,
                                    },
                                ]}
                            />
                        </Section>

                        <Section
                            title="Keys and counters"
                            description="The check's keys, their counters and flags. Open a key to flag or clear it."
                        >
                            <ul
                                className="m-0 list-none space-y-3 p-0"
                                data-testid="case-keys"
                            >
                                {c.keys.map((k) => (
                                    <li
                                        key={`${k.kind}:${k.key}`}
                                        className="text-sm"
                                    >
                                        <Link
                                            to={keyHref(k.kind, k.key)}
                                            className="font-medium hover:underline"
                                        >
                                            {KIND_LABEL[k.kind] ?? k.kind}
                                        </Link>{' '}
                                        <span className="text-fg-2 font-mono text-xs">
                                            {k.key.slice(0, 12)}
                                        </span>
                                        {k.signal?.flagged_at ? (
                                            <Badge
                                                variant="danger"
                                                className="ml-2"
                                            >
                                                Flagged
                                                {k.signal.flag_reason
                                                    ? `: ${k.signal.flag_reason}`
                                                    : ''}
                                            </Badge>
                                        ) : null}
                                        {k.signal ? (
                                            <span className="text-fg-2 block tabular-nums">
                                                {k.signal.attempts_24h} attempts
                                                and {k.signal.declines_24h}{' '}
                                                declines in 24 h,{' '}
                                                {k.signal.approvals_7d}{' '}
                                                approvals in 7 days,{' '}
                                                {k.signal.refunds_30d} refunds
                                                in 30 days,{' '}
                                                {k.signal.chargebacks_180d}{' '}
                                                chargebacks in 180 days
                                            </span>
                                        ) : (
                                            <span className="text-fg-2 block">
                                                First seen on this payment.
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </Section>

                        <Section
                            title="Identity graph"
                            description={`Keys seen together with this buyer's, to two hops. A flagged key is red; a dashed key has more than ${c.graph.degree_cap} links and spreads nothing.`}
                        >
                            <GraphCanvas
                                nodes={c.graph.nodes}
                                edges={c.graph.edges}
                                label="Identity graph of this case"
                                onSelect={(n) =>
                                    navigate(keyHref(n.kind, n.key))
                                }
                            />
                        </Section>

                        <Section
                            title="Buyer with this merchant"
                            description="The buyer's email key at this merchant over 180 days."
                        >
                            <KeyValueList
                                items={[
                                    {
                                        label: 'Attempts',
                                        value: c.history.attempts ?? 0,
                                        num: true,
                                    },
                                    {
                                        label: 'Approved',
                                        value: (
                                            <>
                                                {c.history.approved ?? 0} (
                                                <Money
                                                    cents={
                                                        c.history
                                                            .approved_cents ?? 0
                                                    }
                                                    currency={c.currency}
                                                />
                                                )
                                            </>
                                        ),
                                        num: true,
                                    },
                                    {
                                        label: 'Declined',
                                        value: c.history.declined ?? 0,
                                        num: true,
                                    },
                                    {
                                        label: 'Refunds',
                                        value: c.history.refunds ?? 0,
                                        num: true,
                                    },
                                    {
                                        label: 'Chargebacks',
                                        value: c.history.chargebacks ?? 0,
                                        num: true,
                                    },
                                    {
                                        label: 'First seen',
                                        value: (
                                            <DateTime
                                                zone="inline"
                                                value={
                                                    c.history.first_seen_at ??
                                                    null
                                                }
                                                empty="—"
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </Section>

                        <Section
                            title="Prior cases"
                            description="Other cases on any of these keys."
                        >
                            {c.prior_cases.length === 0 ? (
                                <p className="text-fg-2 text-sm">None.</p>
                            ) : (
                                <ul className="m-0 list-none space-y-1 p-0 text-sm">
                                    {c.prior_cases.map((p) => (
                                        <li
                                            key={p.id}
                                            className="flex flex-wrap items-center gap-2"
                                        >
                                            <Link
                                                to={caseHref(p.id)}
                                                className="font-mono text-xs hover:underline"
                                            >
                                                {p.id}
                                            </Link>
                                            <StatusBadge
                                                status={p.state}
                                                domain={ADMIN_STATUSES.riskCase}
                                            />
                                            <Money
                                                cents={p.amount_cents}
                                                currency={p.currency}
                                            />
                                            <DateTime
                                                value={p.opened_at}
                                                empty="—"
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section title="Lists">
                            {c.lists.length === 0 ? (
                                <p className="text-fg-2 text-sm">On no list.</p>
                            ) : (
                                <ul className="m-0 list-none space-y-1 p-0 text-sm">
                                    {c.lists.map((l) => (
                                        <li key={`${l.list}:${l.value_kind}`}>
                                            {statusLabel(l.list ?? '')}:{' '}
                                            {KIND_LABEL[l.value_kind ?? ''] ??
                                                statusLabel(l.value_kind ?? '')}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section title="Notes">
                            <div className="space-y-3">
                                {c.notes.length === 0 ? (
                                    <p className="text-fg-2 text-sm">
                                        No notes yet.
                                    </p>
                                ) : (
                                    <ul
                                        className="m-0 list-none space-y-2 p-0 text-sm"
                                        data-testid="case-notes"
                                    >
                                        {c.notes.map((n) => (
                                            <li key={n.id}>
                                                <span className="text-fg-2">
                                                    {who(n.author, me)},{' '}
                                                    <DateTime
                                                        value={n.created_at}
                                                        empty="—"
                                                    />
                                                </span>
                                                <span className="block whitespace-pre-wrap">
                                                    {n.body}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {decider ? (
                                    <form
                                        method="post"
                                        className="space-y-2"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            if (note.trim() === '') return;
                                            addNote.mutate(note.trim(), {
                                                onSuccess: () => setNote(''),
                                            });
                                        }}
                                    >
                                        <Label htmlFor="case-note">
                                            Add a note
                                        </Label>
                                        <Textarea
                                            id="case-note"
                                            value={note}
                                            onChange={(e) =>
                                                setNote(e.target.value)
                                            }
                                            rows={3}
                                        />
                                        <Button
                                            type="submit"
                                            size="sm"
                                            variant="outline"
                                            disabled={
                                                addNote.isPending ||
                                                note.trim() === ''
                                            }
                                        >
                                            Add note
                                        </Button>
                                    </form>
                                ) : null}
                            </div>
                        </Section>

                        {decision && decision.kind !== 'decline' ? (
                            <ReasonDialog
                                open
                                onOpenChange={(o) =>
                                    o ? null : setDecision(null)
                                }
                                title={COPY[decision.kind].title}
                                description={COPY[decision.kind].description}
                                confirmLabel={COPY[decision.kind].confirm}
                                required
                                busy={decide.isPending}
                                error={decide.error}
                                onConfirm={(reason) =>
                                    decide.mutate(
                                        {
                                            ...decision,
                                            reason,
                                            version: c.version,
                                            flagKeys: [],
                                        },
                                        {
                                            onSuccess: () => setDecision(null),
                                            onError: refused,
                                        },
                                    )
                                }
                            />
                        ) : null}
                        {decision?.kind === 'decline' ? (
                            <ConfirmDialog
                                open
                                onCancel={() => setDecision(null)}
                                title={COPY.decline.title}
                                body={COPY.decline.description}
                                confirmLabel={COPY.decline.confirm}
                                destructive
                                loading={decide.isPending}
                                reason={{ required: true, enforce: true }}
                                error={
                                    decide.error
                                        ? apiErrorMessage(decide.error)
                                        : undefined
                                }
                                onConfirm={({ reason }) =>
                                    decide.mutate(
                                        {
                                            ...decision,
                                            reason,
                                            version: c.version,
                                            flagKeys: flags,
                                        },
                                        {
                                            onSuccess: () => setDecision(null),
                                            onError: refused,
                                        },
                                    )
                                }
                            >
                                <fieldset
                                    className="m-0 space-y-2 border-0 p-0"
                                    data-testid="decline-flags"
                                >
                                    <legend className="mb-1 text-sm font-medium">
                                        Flag these keys
                                    </legend>
                                    {FLAGGABLE.filter(([kind]) =>
                                        c.keys.some((k) => k.kind === kind),
                                    ).map(([kind, label]) => (
                                        <div
                                            key={kind}
                                            className="flex items-center gap-2"
                                        >
                                            <Checkbox
                                                id={`flag-${kind}`}
                                                checked={flags.includes(kind)}
                                                onCheckedChange={(v) =>
                                                    setFlags((f) =>
                                                        v === true
                                                            ? [...f, kind]
                                                            : f.filter(
                                                                  (x) =>
                                                                      x !==
                                                                      kind,
                                                              ),
                                                    )
                                                }
                                            />
                                            <Label htmlFor={`flag-${kind}`}>
                                                {label}
                                            </Label>
                                        </div>
                                    ))}
                                </fieldset>
                            </ConfirmDialog>
                        ) : null}
                    </div>
                )}
            </DetailBody>
        </div>
    );
}
